import { useCallback, useMemo } from "react";
import { NumberLike } from "@visx/scale";

import { DataPoint } from "types/generated";

import {
  convertDisplayValueToSeconds,
  convertToDisplayValue,
  DORA_AXIS_LABELS,
  DoraTimeUnit,
  formatDoraDuration,
  formatDoraTickLabel,
} from "../helpers";
import { DoraGradeThresholds } from "../getDoraGrade";
import { DoraChartType } from "../useChartType";
import DoraRange from "./DoraRange";

type DoraTimeRangeProps = {
  range: DataPoint[];
  unit: DoraTimeUnit;
  chartType: DoraChartType;
  gradeThresholds?: DoraGradeThresholds;
};

const DoraTimeRange = ({ range, unit, chartType, gradeThresholds }: DoraTimeRangeProps) => {
  const convertValue = useCallback(
    (seconds: number) => convertToDisplayValue(seconds, unit),
    [unit]
  );

  const formatYAxisLabel = useCallback(
    (value: NumberLike) => formatDoraTickLabel(Math.round(value.valueOf()), unit),
    [unit]
  );

  const formatTooltipValue = useCallback(
    (value: number) => formatDoraDuration(convertDisplayValueToSeconds(value, unit), unit),
    [unit]
  );

  const gradeFairMax = useMemo(
    () => (gradeThresholds ? convertToDisplayValue(gradeThresholds.fairMax, unit) : undefined),
    [gradeThresholds, unit]
  );

  return (
    <DoraRange
      range={range}
      chartType={chartType}
      leftAxisLabel={DORA_AXIS_LABELS[unit]}
      formatYAxisLabel={formatYAxisLabel}
      formatTooltipValue={formatTooltipValue}
      convertValue={convertValue}
      gradeFairMax={gradeFairMax}
    />
  );
};

export default DoraTimeRange;
